import type { NotificationPrefs } from './notification-prefs.js';

// This device's Web Push subscription (#760 step 2): notifications even when aidev is closed. Once subscribed,
// the service worker shows them and the page no longer does (no duplicates). iOS needs the app installed.

const KEY = 'aidev.push.endpoint';
const LOST_KEY = 'aidev.push.lost';

interface PushApi {
  publicKey: () => Promise<{ publicKey: string }>;
  subscribe: (body: { subscription: unknown; scope: NotificationPrefs; label?: string }) => Promise<{ id: string }>;
  unsubscribe: (endpoint: string) => Promise<void>;
}

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window;
}

/** True when this device receives Web Push: the page then leaves notifications to the service worker */
export function pushEnabledHere(): boolean {
  try {
    return !!localStorage.getItem(KEY);
  } catch {
    return false;
  }
}

function remember(endpoint: string | null): void {
  try {
    if (endpoint) localStorage.setItem(KEY, endpoint);
    else localStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: the page may show a duplicate, nothing worse
  }
}

function rememberedEndpoint(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function markLost(lost: boolean): void {
  try {
    if (lost) localStorage.setItem(LOST_KEY, '1');
    else localStorage.removeItem(LOST_KEY);
  } catch {
    // not shown in the settings, nothing worse
  }
}

/** True when this device had Web Push and lost it (permission withdrawn): the settings say so */
export function pushLost(): boolean {
  try {
    return localStorage.getItem(LOST_KEY) === '1';
  } catch {
    return false;
  }
}

function keyBytes(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : 'Linux';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${browser} on ${os}`;
}

export async function enablePush(api: PushApi, prefs: NotificationPrefs): Promise<void> {
  if (Notification.permission !== 'granted' && (await Notification.requestPermission()) !== 'granted') {
    throw new Error('Notifications are blocked for this site in the browser settings');
  }
  const registration = await navigator.serviceWorker.ready;
  const { publicKey } = await api.publicKey();
  const subscription = (await registration.pushManager.getSubscription())
    ?? await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource });
  await api.subscribe({ subscription: subscription.toJSON(), scope: prefs, label: deviceLabel() });
  remember(subscription.endpoint);
  markLost(false);
}

export async function disablePush(api: PushApi): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await api.unsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }
  remember(null);
  markLost(false);
}

/**
 * At each opening: browsers sometimes replace a device's push subscription (or drop it). The service worker
 * subscribes again on its own (pushsubscriptionchange) but cannot reach the API without a session, so the page
 * sends the new subscription and retires the old one. If the permission was withdrawn, the device is marked
 * lost and the settings ask to turn notifications on again.
 */
export async function renewPush(api: PushApi, prefs: NotificationPrefs): Promise<'off' | 'ok' | 'renewed' | 'lost'> {
  const previous = rememberedEndpoint();
  if (!previous || !pushSupported()) return 'off';
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (subscription?.endpoint === previous) return 'ok';
  if (!subscription) {
    if (Notification.permission !== 'granted') {
      await api.unsubscribe(previous).catch(() => undefined);
      remember(null);
      markLost(true);
      return 'lost';
    }
    const { publicKey } = await api.publicKey();
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource });
  }
  await api.subscribe({ subscription: subscription.toJSON(), scope: prefs, label: deviceLabel() });
  await api.unsubscribe(previous).catch(() => undefined);
  remember(subscription.endpoint);
  return 'renewed';
}

/** Keeps the server's copy of this device's choice of servers in step */
export async function syncPushScope(api: PushApi, prefs: NotificationPrefs): Promise<void> {
  if (!pushEnabledHere() || !pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) { remember(null); return; }
  await api.subscribe({ subscription: subscription.toJSON(), scope: prefs, label: deviceLabel() });
}
